import { useState, useEffect } from "react";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Ban, Mail } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function BulkReject({ selectedIds, open, onOpenChange, onDone }) {
  const { t } = useTranslation();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && !message) {
      api.get("/message-templates").then((r) => setMessage(r.data.rejection || "")).catch(() => {});
    }
  }, [open, message]);

  const submit = async () => {
    setSending(true);
    try {
      const res = await api.post("/applications/bulk-reject-notify", { ids: selectedIds, message });
      toast.success(res.data.message);
      if (!res.data.email_configured) {
        toast.warning(t("admin.reject.not_configured"));
      }
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.reject.failed"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-destructive" /> {t("admin.reject.title", { count: selectedIds.length })}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <p className="text-sm text-muted-foreground leading-relaxed">
            <Trans i18nKey="admin.reject.intro" components={{ b: <b /> }} />
          </p>
          <div>
            <Label className="text-sm mb-2 block">{t("admin.reject.message")}</Label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={12} className="text-xs leading-relaxed" data-testid="reject-message" />
            <p className="text-xs text-muted-foreground mt-1">{t("admin.reject.name_hint")}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{t("common.cancel")}</Button>
          <Button variant="destructive" onClick={submit} disabled={sending || selectedIds.length === 0} data-testid="confirm-reject-btn">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.sending")}</> : <><Mail className="w-4 h-4 mr-2" /> {t("admin.reject.submit")}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
